import React, { useState, useRef, useEffect } from 'react';

const ReasonDropdown = ({ title, item, localReason, onChange, reasonOptions }) => {
  const [searchTerm, setSearchTerm] = useState(localReason || '');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef(null);

  // Filter reasons based on search term
  const filteredReasons = reasonOptions.filter(reason =>
    reason.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Handle clicking outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Update searchTerm when localReason changes
  useEffect(() => {
    setSearchTerm(localReason || '');
  }, [localReason]);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!isDropdownOpen) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < filteredReasons.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && filteredReasons[highlightedIndex]) {
          handleSelect(filteredReasons[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsDropdownOpen(false);
        setHighlightedIndex(-1);
        break;
    }
  };

  // Handle selecting a reason
  const handleSelect = (reason) => {
    setSearchTerm(reason);
    onChange("reason", reason);
    setIsDropdownOpen(false);
    setHighlightedIndex(-1);
  };

  return (
    <div className="flex flex-col">
      <div className="relative" ref={dropdownRef}>
        {title && <label 
          htmlFor={`reason-${item._id}`}
          className="font-semibold text-black mb-1 block"
        >
          {title }: 
        </label>}
        <input
          type="text"
          id={`reason-${item._id}`}
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setIsDropdownOpen(true);
            onChange("reason", e.target.value);
            setHighlightedIndex(-1);
          }}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            setIsDropdownOpen(true);
            setHighlightedIndex(-1);
          }}
          className="mt-1 block w-full rounded-lg px-4 py-2 bg-white text-gray-800 focus:ring-2 focus:ring-blue-500 border border-gray-300 transition-all"
          placeholder="Select a reason"
          required
        />
        
        {/* Dropdown with reason options */}
        {isDropdownOpen && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-[300px] overflow-auto">
            <div className="grid grid-cols-1 gap-1">
              {filteredReasons.length > 0 ? (
                filteredReasons.map((reason, index) => (
                  <div
                    key={`${reason}-${index}`}
                    onClick={() => handleSelect(reason)}
                    className={`flex items-center gap-3 p-3 hover:bg-gray-300 cursor-pointer dropdown-item transition-colors ${
                      index === highlightedIndex ? "bg-gray-400" : ""
                    }`}
                  >
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{reason}</p>
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-3 text-gray-500 text-center">
                  No reasons found
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReasonDropdown;

// Usage example:
/*
<ReasonDropdown
  title="Reason For Appointment"
  item={item}
  localReason={reason}
  onChange={(field, value) => setReason(value)}
  reasonOptions={reasonOptions}
/>
*/